import { useGetApPayeesBulkInvitationsAvailabilities } from '@/context/services/doc_instructions/DocInstructions.service';
import { SearchedApPayee } from 'ap-openapi';
import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to check bulk invitation availabilities emails for payees
 * this is using in case we edit payee success, but we do not reload the page
 */

export const useCheckBulkInvitationAvailabilities = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [
    shouldCheckBulkInvitationAvailabilities,
    setShouldCheckBulkInvitationAvailabilities,
  ] = useState(false);
  const [bulkEmailAvailable, setBulkEmailAvailable] = useState<boolean | null>(
    null
  );

  const handleSetCheckBulkInvitationAvailabilities = useCallback(() => {
    setShouldCheckBulkInvitationAvailabilities(true);
  }, []);

  /** Effect listen to edit payee success event
   * When user edit payee success, set shouldCheckBulkInvitationAvailabilities to true
   * and check bulk invitation availabilities again
   */
  useEffect(() => {
    window.addEventListener(
      'edit-payee-success',
      handleSetCheckBulkInvitationAvailabilities
    );

    return () => {
      window.removeEventListener(
        'edit-payee-success',
        handleSetCheckBulkInvitationAvailabilities
      );
    };
  }, [handleSetCheckBulkInvitationAvailabilities]);

  const { handleGetApPayeesBulkInvitationsAvailabilities } =
    useGetApPayeesBulkInvitationsAvailabilities();

  const handleCheckBulkInvitationAvailabilities = useCallback(
    async (payees: SearchedApPayee[]) => {
      const apPayeeIds = payees.map((payee) => payee.id ?? '').filter(Boolean);
      if (apPayeeIds.length === 0) return;
      setIsChecking(true);
      try {
        const resp = await handleGetApPayeesBulkInvitationsAvailabilities(
          apPayeeIds
        );
        if (resp?.status === 'success') {
          setBulkEmailAvailable(resp?.data?.bulk_email_available ?? null);
        } else {
          setBulkEmailAvailable(null);
        }
      } catch (error) {
        setBulkEmailAvailable(null);
      } finally {
        setIsChecking(false);
      }
    },
    [handleGetApPayeesBulkInvitationsAvailabilities]
  );

  return {
    shouldCheckBulkInvitationAvailabilities,
    handleCheckBulkInvitationAvailabilities,
    isChecking,
    bulkEmailAvailable,
  };
};
